import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { CONSTANT } from "src/app/constant";
import { OBAttempt } from "src/app/models/models";
import { SharedService } from "src/app/shared/services/shared.service";
import { makeObsValue } from "../ts1-form.factories";

type ObRole = "pf" | "pm";

@Component({
	selector: "app-ts1-assessment-ob-table",
	templateUrl: "./ts1-assessment-ob-table.component.html",
	styleUrls: ["./ts1-assessment-ob-table.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class Ts1AssessmentObTableComponent implements OnInit, OnChanges {
	@Input() maneuverForm: UntypedFormGroup | null = null;
	@Input() maneuverAction: "create-form" | "edit-form" = "create-form";
	@Input() previewMode = false;
	@Input() readOnly = false;
	@Input() hideRoleHeader = false;
	@Input() formTypeCode: string | null = null;

	TS1_OB_LIST: Array<{ title: string; name: string; desc: string; item_desc: Array<{ description: string }> }> = [];

	private readonly swipeThresholdPx = 40;
	private readonly revealWidthPx = 65;
	private pointerStarts = new Map<string, { x: number; y: number; dragging: boolean }>();
	private swipeOffsets = new Map<string, number>();
	private actionModeCells = new Set<string>();
	private swipeRevision = 0;

	constructor(
		public sharedService: SharedService,
		private cd: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadObList();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["previewMode"] || changes["readOnly"] || changes["formTypeCode"] || changes["maneuverForm"]) {
			this.loadObList();
			this.resetSwipeState();
		}
	}

	private loadObList(): void {
		const ft = this.previewMode ? (this.formTypeCode ?? CONSTANT.FORM_TYPE.ts_1) : this.maneuverForm?.value?.formType;
		const code = typeof ft === "string" ? ft : (ft?.code ?? ft ?? CONSTANT.FORM_TYPE.ts_1);
		this.TS1_OB_LIST = this.sharedService.generateSortedOBList(code) ?? [];
	}

	private resetSwipeState(): void {
		this.pointerStarts.clear();
		this.swipeOffsets.clear();
		this.actionModeCells.clear();
		this.swipeRevision = 0;
	}

	isSectionExpanded(title: string): boolean {
		if (this.previewMode && !this.maneuverForm) {
			return false;
		}
		return this.hasValue(title);
	}

	hasValue(title: string): boolean {
		const group = this.maneuverForm?.get(title) as UntypedFormGroup | null;
		if (!group) return false;
		for (const obKey of Object.keys(group.controls)) {
			const cell = group.get(obKey) as UntypedFormGroup | null;
			const pf = this.getRoleValue(cell, "pf");
			const pm = this.getRoleValue(cell, "pm");
			if ((pf.currentCount ?? 0) > 0 || (pm.currentCount ?? 0) > 0 || pf.isConditional || pm.isConditional) {
				return true;
			}
		}
		return false;
	}

	getObLabel(title: string, index: number): string {
		return `${title} ${index + 1}`;
	}

	hasRoleCount(obCell: unknown, role: ObRole): boolean {
		const cell = obCell as UntypedFormGroup | null;
		return (this.getRoleValue(cell, role).currentCount ?? 0) > 0;
	}

	isRoleConditional(obCell: unknown, role: ObRole): boolean {
		const cell = obCell as UntypedFormGroup | null;
		return this.getRoleValue(cell, role).isConditional === true;
	}

	cellKey(title: string, index: number, role: ObRole): string {
		return `${title}:${index}:${role}`;
	}

	isActionMode(title: string, index: number, role: ObRole): boolean {
		return this.actionModeCells.has(this.cellKey(title, index, role));
	}

	isDragging(title: string, index: number, role: ObRole): boolean {
		void this.swipeRevision;
		return this.swipeOffsets.has(this.cellKey(title, index, role));
	}

	getRevealVisibleWidth(title: string, index: number, role: ObRole): number {
		void this.swipeRevision;
		const key = this.cellKey(title, index, role);
		if (this.swipeOffsets.has(key)) {
			return Math.min(this.revealWidthPx, Math.abs(this.swipeOffsets.get(key)!));
		}
		return this.isActionMode(title, index, role) ? this.revealWidthPx : 0;
	}

	cellStateClass(_obCell: unknown, role: ObRole, title: string, index: number): Record<string, boolean> {
		return {
			"ts1-ob-cell--action-mode": this.isActionMode(title, index, role),
		};
	}

	dotStateClass(obCell: unknown, role: ObRole): Record<string, boolean> {
		if (this.isRoleConditional(obCell, role)) {
			return { "ts1-ob-dot--conditional": true };
		}
		return {
			"ts1-ob-dot--active": this.hasRoleCount(obCell, role),
			"ts1-ob-dot--idle": !this.hasRoleCount(obCell, role),
		};
	}

	onCellPointerDown(ev: PointerEvent, title: string, index: number, role: ObRole): void {
		if (this.previewMode || this.readOnly || ev.button !== 0) return;
		if (this.isFromInteractive(ev)) return;

		const key = this.cellKey(title, index, role);
		this.pointerStarts.set(key, { x: ev.clientX, y: ev.clientY, dragging: false });
		(ev.currentTarget as HTMLElement)?.setPointerCapture?.(ev.pointerId);
	}

	onCellPointerMove(ev: PointerEvent, title: string, index: number, role: ObRole): void {
		const key = this.cellKey(title, index, role);
		const start = this.pointerStarts.get(key);
		if (!start) return;

		const deltaX = ev.clientX - start.x;
		const deltaY = ev.clientY - start.y;

		if (!start.dragging) {
			if (Math.abs(deltaX) < 8 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
			start.dragging = true;
		}

		const baseOffset = this.isActionMode(title, index, role) ? -this.revealWidthPx : 0;
		const next = Math.max(-this.revealWidthPx, Math.min(0, baseOffset + deltaX));
		this.swipeOffsets.set(key, next);
		this.bumpSwipeRevision();
	}

	onCellPointerUp(ev: PointerEvent, title: string, index: number, role: ObRole): void {
		const key = this.cellKey(title, index, role);
		const start = this.pointerStarts.get(key);
		this.pointerStarts.delete(key);
		(ev.currentTarget as HTMLElement)?.releasePointerCapture?.(ev.pointerId);

		if (!start || this.isFromInteractive(ev)) {
			this.swipeOffsets.delete(key);
			this.bumpSwipeRevision();
			return;
		}

		const deltaX = ev.clientX - start.x;
		const deltaY = ev.clientY - start.y;
		const currentOffset =
			this.swipeOffsets.get(key) ?? (this.isActionMode(title, index, role) ? -this.revealWidthPx : 0);
		this.swipeOffsets.delete(key);
		this.bumpSwipeRevision();

		const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
		if (start.dragging && isHorizontal) {
			if (deltaX <= -this.swipeThresholdPx || currentOffset <= -this.revealWidthPx / 2) {
				this.showActionMode(title, index, role);
				return;
			}
			if (deltaX >= this.swipeThresholdPx || currentOffset > -this.revealWidthPx / 2) {
				this.closeActionMode(title, index, role);
				return;
			}
		}
	}

	onCellPointerCancel(title: string, index: number, role: ObRole): void {
		const key = this.cellKey(title, index, role);
		this.pointerStarts.delete(key);
		if (this.swipeOffsets.has(key)) {
			const currentOffset = this.swipeOffsets.get(key)!;
			this.swipeOffsets.delete(key);
			this.bumpSwipeRevision();
			if (currentOffset <= -this.revealWidthPx / 2) {
				this.showActionMode(title, index, role);
			} else {
				this.closeActionMode(title, index, role);
			}
		}
	}

	getConditionalActionLabel(isConditional: boolean): string {
		return isConditional ? "Unmark as conditional OB" : "Mark as conditional OB";
	}

	onToggleConditional(obCell: UntypedFormGroup, role: ObRole, title: string, index: number): void {
		if (this.readOnly) return;
		const control = obCell.get(role);
		if (!control) return;

		const current = this.getRoleValue(obCell, role);
		const nextConditional = !current.isConditional;
		const lastAction =
			this.maneuverAction === "edit-form" ? CONSTANT.OB_LAST_ACTION.EDIT : CONSTANT.OB_LAST_ACTION.CREATE;

		control.patchValue({
			...current,
			isConditional: nextConditional,
			totalCount: nextConditional ? 0 : current.totalCount,
			currentCount: nextConditional ? 0 : current.currentCount,
			lastAction,
		});

		this.closeActionMode(title, index, role);
		this.cd.markForCheck();
	}

	private showActionMode(title: string, index: number, role: ObRole): void {
		this.actionModeCells.add(this.cellKey(title, index, role));
		this.bumpSwipeRevision();
		this.cd.markForCheck();
	}

	private closeActionMode(title: string, index: number, role: ObRole): void {
		const key = this.cellKey(title, index, role);
		this.actionModeCells.delete(key);
		this.swipeOffsets.delete(key);
		this.pointerStarts.delete(key);
		this.bumpSwipeRevision();
		this.cd.markForCheck();
	}

	private bumpSwipeRevision(): void {
		this.swipeRevision++;
	}

	private getRoleValue(obCell: UntypedFormGroup | null, role: ObRole): OBAttempt {
		const raw = obCell?.get(role)?.value;
		return (raw ?? makeObsValue()) as OBAttempt;
	}

	private isFromInteractive(ev: Event): boolean {
		const target = ev.target as HTMLElement | null;
		return !!target?.closest("button, a, input, textarea, select, mat-icon, .attemptBtn, .actionBtn");
	}
}
